import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// Utility to slugify heading text for id attributes
const slugify = (text) =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const BlogPage = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const rawUrl = params.get("url") || "";
  const s3Url = decodeURIComponent(rawUrl);

  const [htmlContent, setHtmlContent] = useState("");
  const [toc, setToc] = useState([]);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!s3Url) {
      setError("No blog URL provided in query string.");
      return;
    }
    console.log("Fetching blog from:", s3Url);

    const fetchAndProcess = async () => {
      try {
        const response = await fetch(s3Url);
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        const text = await response.text();

        // Extract title, date, and cover image from HTML
        const titleMatch = text.match(/<h1[^>]*>(.*?)<\/h1>/i);
        const dateMatch = text.match(/<p[^>]*text-gray-500[^>]*>(.*?)<\/p>/i);
        const imgMatch = text.match(/<img[^>]*src=["']([^"']+)["'][^>]*>/i);
        if (titleMatch) setTitle(titleMatch[1]);
        if (dateMatch) setDate(dateMatch[1]);
        if (imgMatch) setCoverUrl(imgMatch[1]);

        // Parse HTML into a DOM
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, "text/html");

        // Generate table of contents from h2 and h3
        const headings = Array.from(doc.querySelectorAll("h2, h3"));
        const tocItems = headings.map(node => {
          const txt = node.textContent;
          const id = slugify(txt);
          node.setAttribute("id", id);
          return { id, text: txt, level: node.tagName.toLowerCase() === 'h2' ? 2 : 3 };
        });
        setToc(tocItems);

        // Extract article content
        const articleEl = doc.querySelector("article");
        const contentHtml = articleEl ? articleEl.innerHTML : doc.body.innerHTML;
        setHtmlContent(contentHtml);
      } catch (err) {
        console.error(err);
        setError(err.message);
      }
    };

    fetchAndProcess();
  }, [s3Url]);

  if (error) {
    return (
      <div className="container mx-auto px-4 py-10">
        <p className="text-red-600">Error loading blog: {error}</p>
      </div>
    );
  }

  return (
    <section className="py-12 bg-white sm:py-16 lg:py-20">
      <div className="px-4 mx-auto sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-5xl mx-auto">
          {/* Header Section */}
          <div className="max-w-2xl mx-auto">
            {title && <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">{title}</h1>}
            {date && <p className="mt-6 text-base font-medium text-gray-500">{date}</p>}
          </div>

          {/* Cover Image */}
          {coverUrl && (
            <div className="mt-12 sm:mt-16 lg:grid lg:grid-cols-12 lg:gap-x-16 xl:gap-x-24">
              <img
                className="object-cover w-full h-auto rounded-lg shadow-md lg:col-span-8"
                src={coverUrl}
                alt={title}
              />
            </div>
          )}

          <div className="mt-12 sm:mt-16 lg:grid lg:grid-cols-12 lg:gap-x-16 xl:gap-x-24">
            {/* Table of Contents */}
            <aside className="lg:col-span-4 lg:order-last lg:self-start lg:sticky lg:top-8">
              <div className="overflow-hidden bg-white border border-gray-200">
                <div className="px-4 py-5 sm:p-6">
                  <h4 className="text-xs font-bold tracking-widest text-gray-400 uppercase">On this page</h4>
                  <ul className="mt-8 space-y-5">
                    {toc.map(({ id, text, level }) => (
                      <li key={id}>
                        <a
                          href={`#${id}`}
                          className={`flex text-base font-bold hover:underline ${
                            level === 2 ? 'text-gray-900' : 'text-gray-500'
                          }`}
                        >
                          {text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>

            {/* Article Content */}
            <article className="mt-12 prose lg:mt-0 lg:prose-lg lg:col-span-8 prose-blockquote:lg-text-xl prose-blockquote:lg-leading-9 prose-blockquote:border-none prose-blockquote:text-lg prose-blockquote:leading-8 prose-blockquote:p-0 prose-blockquote:font-medium prose-blockquote:text-gray-900">
              <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogPage;
